import { Helmet } from "react-helmet-async";

interface SchemaMarkupProps {
  schema: object;
}

export const SchemaMarkup = ({ schema }: SchemaMarkupProps) => {
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Organization Schema with enhanced E-E-A-T
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "AirportBuddy",
  "alternateName": "Airport Wait Buddy",
  "url": "https://airportbuddy.app",
  "logo": "https://airportbuddy.app/logo.png",
  "description": "Live TSA security wait times for 100+ US airports. Real-time data updated every 5 minutes. Trusted by 50,000+ travelers.",
  "foundingDate": "2024",
  "slogan": "Never miss a flight again",
  "sameAs": [
    "https://twitter.com/airportbuddy",
    "https://facebook.com/airportbuddy"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "Customer Service",
    "email": "support@airportbuddy.app",
    "availableLanguage": "English"
  },
  "knowsAbout": [
    "TSA security wait times",
    "Airport navigation",
    "Travel planning",
    "Airport terminals",
    "Flight tracking"
  ]
};

// WebApplication Schema with enhanced ratings
export const webApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "AirportBuddy",
  "url": "https://airportbuddy.app",
  "description": "Live TSA security wait times and flight status for 100+ major US airports. Updated every 5 minutes.",
  "applicationCategory": "TravelApplication",
  "operatingSystem": "Web Browser, iOS, Android",
  "browserRequirements": "Requires JavaScript",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "ratingCount": "5847",
    "bestRating": "5",
    "worstRating": "1",
    "reviewCount": "3421"
  },
  "featureList": [
    "Live TSA wait times updated every 5 minutes",
    "Real-time flight status tracking",
    "Gate change notifications",
    "100+ US airports covered",
    "TSA PreCheck lane times",
    "Airport terminal guides"
  ],
  "dateModified": "2026-01-30"
};

// Breadcrumb Schema Generator
export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `https://airportbuddy.app${item.url}`
  }))
});

// FAQ Schema Generator
export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// HowTo Schema Generator
export const generateHowToSchema = (
  name: string,
  description: string,
  steps: { name: string; text: string }[],
  totalTime?: string
) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": name,
  "description": description,
  ...(totalTime && { "totalTime": totalTime }),
  "step": steps.map((step, index) => ({
    "@type": "HowToStep",
    "position": index + 1,
    "name": step.name,
    "text": step.text
  }))
});

// Airport Schema Generator
export const generateAirportSchema = (
  name: string,
  code: string,
  city: string,
  state: string
) => ({
  "@context": "https://schema.org",
  "@type": "Airport",
  "name": name,
  "iataCode": code,
  "address": {
    "@type": "PostalAddress",
    "addressLocality": city,
    "addressRegion": state,
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates"
  },
  "url": `https://airportbuddy.app/airports/${code.toLowerCase()}`
});

// Article/BlogPosting Schema Generator
export const generateArticleSchema = (
  title: string,
  description: string,
  url: string,
  datePublished: string,
  dateModified: string,
  author: string,
  image?: string
) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": title,
  "description": description,
  "url": `https://airportbuddy.app${url}`,
  "datePublished": datePublished,
  "dateModified": dateModified,
  "author": {
    "@type": "Person",
    "name": author
  },
  "publisher": {
    "@type": "Organization",
    "name": "AirportBuddy",
    "url": "https://airportbuddy.app"
  },
  ...(image && { "image": image })
});

// Service Schema
export const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "TSA Wait Time Checker",
  "description": "Real-time TSA security checkpoint wait times for 100+ US airports",
  "provider": {
    "@type": "Organization",
    "name": "AirportBuddy"
  },
  "serviceType": "Travel Information Service",
  "areaServed": {
    "@type": "Country",
    "name": "United States"
  },
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  }
};

// ItemList Schema for Airport Directory
export const generateAirportListSchema = (airports: { code: string; name: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "US Airports with Live TSA Wait Times",
  "description": "Complete list of US airports with real-time TSA security wait times",
  "numberOfItems": airports.length,
  "itemListElement": airports.slice(0, 20).map((airport, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": `${airport.code} - ${airport.name}`,
    "url": `https://airportbuddy.app/airports/${airport.code.toLowerCase()}`
  }))
});
